import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

import {
  axiosInstance,
  getAuthToken,
  getRefreshToken,
  saveAuthTokens,
  clearAuthTokens,
  axiosNSInsance,
} from "../utils/axios";

const UserContext = createContext({
  user: null,
  loading: false,
  error: null,
  refreshUser: async () => {},
  logout: async () => {},
  setLoading: () => {},
});

export const UserProvider = ({ children }) => {
  const location = useLocation();
  const isAuthCallback = location.pathname === "/auth/discord/callback";

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(
    Boolean(getAuthToken()) || Boolean(getRefreshToken()) || isAuthCallback,
  );
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (user) {
      localStorage.setItem("discordId", user.discordUser?.discordId || "");
      axiosNSInsance.post("/analytics/auth", {
        user,
        type: "login",
      });
    }
  }, [user]);

  useEffect(() => {
    if (isAuthCallback && !loading) {
      setLoading(true);
    }
  }, [isAuthCallback, loading]);

  const fetchUser = useCallback(async (force = false) => {
    if (isAuthCallback && !force) {
      return;
    }

    let token = getAuthToken();
    const refreshToken = getRefreshToken();

    if (!token && refreshToken) {
      try {
        const refreshResponse = await axiosInstance.post(
          `/auth/discord/refresh?token=${refreshToken}`,
        );
        const data = refreshResponse?.data;
        if (data && data.token) {
          saveAuthTokens(data);
          token = data.token;
        }
      } catch (refreshErr) {
        console.error("Initial session refresh failed:", refreshErr);
      }
    }

    if (!token) {
      localStorage.removeItem("discordId");
      setUser(null);
      setHasFetched(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get("/users/@me");
      setUser(response?.data || null);
    } catch (err) {
      setUser(null);
      localStorage.removeItem("discordId");
      setError(err);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchUser(true);
  }, [fetchUser]);

  useEffect(() => {
    if (!hasFetched) {
      fetchUser();
    }
  }, [fetchUser, hasFetched]);

  const logout = useCallback(async () => {
    try {
      const refreshToken = getRefreshToken();
      await axiosInstance.post(`/users/logout${refreshToken ? `?token=${refreshToken}` : ""}`);
    } catch {
    } finally {
      clearAuthTokens();
      setUser(null);
      localStorage.removeItem("discordId");
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      refreshUser,
      logout,
      setLoading,
    }),
    [user, loading, error, refreshUser, logout],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => useContext(UserContext);
